import React, { useState, useEffect, useMemo } from "react";
import {
  Users,
  Save,
  Trash2,
  Plus,
  RefreshCw,
  AlertTriangle,
  Shield,
  CheckSquare,
  Square,
  Mail,
  Phone,
  Camera,
  Settings,
  FileText,
  Truck,
  MessageSquare,
  Clock,
  Search,
  UserCheck,
  ChevronRight,
  Menu,
  X,
  Sparkles,
  Database,
  ArrowLeft,
  Lock,
} from "lucide-react";
import { pb } from "../services/pocketbase";
import LoadingOverlay from "../components/layout/LoadingOverlay";
import CatalogImporter from "../components/CatalogImporter";
import IaConfigSubTab from "./IaConfigSubTab";
import FiscalConfigPanel from "../components/fiscal/FiscalConfigPanel";
import { planningService } from "../services/planningService";
import CorreiosConfigPanel from "../components/correios/CorreiosConfigPanel";
import OperatorsConfigPanel from "../components/oficina/OperatorsConfigPanel";
import ChannelsConfigPanel from "../components/communication/ChannelsConfigPanel";
import TeamsConfigPanel from "../components/communication/TeamsConfigPanel";
import PrazosConfigTab from "./PrazosConfigTab";

// --- MAPEAMENTO DAS PERMISSÕES (CATEGORIZADAS) ---
const MODULE_CATEGORIES = [
  {
    id: "operacoes",
    title: "Operações & Logística",
    badgeColor: "bg-blue-100 text-blue-800 border-blue-200",
    modules: [
      { id: "production", label: "Produção (Kanban)", desc: "Acompanhamento das etapas de produção" },
      { id: "orders", label: "Pedidos (Logística)", desc: "Gerenciamento e status de pedidos" },
      { id: "postagem", label: "Expedição (Postagem)", desc: "Despachos, etiquetas e envios Correios" },
      { id: "reservations", label: "Reservas (Separar)", desc: "Separação de peças reservadas do estoque" },
      { id: "transit", label: "Trânsito (Interno)", desc: "Transferências internas e lotes em trânsito" },
      { id: "motoboy", label: "Motoboy (Entregas)", desc: "Controle de rotas e entregas por motoboy" },
    ]
  },
  {
    id: "estoque",
    title: "Estoque & Suprimentos",
    badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-200",
    modules: [
      { id: "stock", label: "Estoque (Visualização Geral)", desc: "Saldo e visualização de itens físicos" },
      { id: "stock_production", label: "Fábrica (Produção Estoque)", desc: "Lotes de produção interna para reposição" },
      { id: "supplies", label: "Insumos & Matriz BOM", desc: "Matérias-primas, pedras e embalagens" },
      { id: "planejamento", label: "Planejamento", desc: "Cálculo de reposição e estoque mínimo" },
      { id: "conference", label: "Conferência (Bipar)", desc: "Sub-recurso de bipagem de conferência física", isSubFeature: true },
      { id: "sales", label: "Baixa / Vendas", desc: "Sub-recurso de baixa de estoque e vendas", isSubFeature: true },
    ]
  },
  {
    id: "criacao",
    title: "Criação & Desenvolvimento",
    badgeColor: "bg-purple-100 text-purple-800 border-purple-200",
    modules: [
      { id: "oficina", label: "Oficina & Ourives", desc: "Bancadas, consertos e serviços de oficina" },
      { id: "novos_produtos", label: "Novos Produtos & Modelagem", desc: "Modelagem 3D CAD, fichas técnicas e cotação" },
    ]
  },
  {
    id: "gestao",
    title: "Gestão, Fiscal & Administrativo",
    badgeColor: "bg-amber-100 text-amber-800 border-amber-200",
    modules: [
      { id: "dashboard", label: "Dashboard (Gráficos)", desc: "Visão executiva consolidada de indicadores" },
      { id: "financeiro", label: "Financeiro & DRE", desc: "Contas, conciliação e relatórios financeiros" },
      { id: "fiscal", label: "Área Fiscal (NFe & Regras)", desc: "Emissão de notas e parametrizações fiscais" },
      { id: "reports", label: "Relatórios & Logs", desc: "Auditoria de acessos e movimentações" },
      { id: "archived", label: "Arquivo Morto", desc: "Histórico de itens e pedidos arquivados" },
      { id: "colaboradores", label: "Colaboradores (Gestão de Equipes & Ponto)", desc: "Gestão de espelho de ponto, escalas e aprovações" },
      { id: "pontuacao", label: "Pontos & Bônus", desc: "Painel consolidado de metas e pontuações" },
      { id: "config", label: "Configurações (Admin Geral)", desc: "Acesso irrestrito a configurações e usuários" },
    ]
  },
  {
    id: "comercial",
    title: "Comercial & Balcão",
    badgeColor: "bg-indigo-100 text-indigo-800 border-indigo-200",
    modules: [
      { id: "pdv", label: "PDV Showroom", desc: "Frente de caixa para vendas balcão e showroom" },
    ]
  }
];

const AVAILABLE_MODULES = MODULE_CATEGORIES.flatMap((c) => c.modules);

export default function ConfigTab() {
  const [users, setUsers] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  
  // Subaba ativa na sidebar: "usuarios" | "geral" | "ia" | "prazos" | "operadores" | "equipes" | "canais" | "correios" | "fiscal"
  const [configActiveSubTab, setConfigActiveSubTab] = useState("usuarios");
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);

  const [configRecord, setConfigRecord] = useState(null);
  const [prazoDias, setPrazoDias] = useState(15);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const conf = await planningService.fetchConfig();
        setConfigRecord(conf);
        setPrazoDias(conf.prazo_fabricacao_dias || 15);
      } catch (err) {
        console.error("Error fetching config in ConfigTab:", err);
      }
    };
    loadConfig();
  }, []);

  const handleSavePrazo = async (val) => {
    const num = Number(val);
    if (isNaN(num) || num < 1) return;
    setPrazoDias(num);
    try {
      if (configRecord) {
        const updated = await planningService.updateConfig(
          configRecord.id,
          {
            ...configRecord,
            prazo_fabricacao_dias: num,
          },
          pb.authStore.model?.id
        );
        setConfigRecord(updated);
      } else {
        const created = await planningService.updateConfig(
          null,
          {
            prazo_fabricacao_dias: num,
            buffer_seguranca_percentual: 30,
            janela_historico_meses: 6,
          },
          pb.authStore.model?.id
        );
        setConfigRecord(created);
      }
    } catch (err) {
      console.error("Error updating config in ConfigTab:", err);
    }
  };

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    username: "",
    email: "",
    phone: "",
    password: "",
    oldPassword: "",
    role: "user",
    acess: [],
    avatar: null,
    avatarUrl: "",
    taxa_motoboy: 0,
  });

  const isSelf = pb.authStore.model && pb.authStore.model.id === formData.id;
  const originalUser = users.find((u) => u.id === formData.id);
  const hasUsernameChanged = !!(
    originalUser && formData.username.trim() !== originalUser.username
  );
  const hasPasswordChanged = !!formData.password;
  const requiresOldPassword = !!(
    isSelf && (hasUsernameChanged || hasPasswordChanged)
  );

  // --- CARREGAR USUÁRIOS (Realtime) ---
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const records = await pb.collection("users").getFullList({ sort: "name" });
        setUsers(records);
      } catch (e) {
        console.error("Erro listing users:", e);
      }
    };

    fetchUsers();

    pb.collection("users").subscribe("*", (e) => {
      if (e.action === "update") {
        setUsers((prev) => prev.map((u) => (u.id === e.record.id ? e.record : u)));
      } else if (e.action === "create") {
        setUsers((prev) => [...prev, e.record]);
      } else if (e.action === "delete") {
        setUsers((prev) => prev.filter((u) => u.id !== e.record.id));
      }
    });

    return () => {
      pb.collection("users").unsubscribe("*").catch(() => {});
    };
  }, []);

  // --- AÇÕES ADMINISTRATIVAS ---
  const handleResetStock = async () => {
    if (
      !window.confirm(
        "ATENÇÃO: Isso removerá TODOS os itens físicos do estoque (mudando o status para 'adjusted_out'). O histórico será mantido.\n\nDeseja continuar?"
      )
    )
      return;

    const code = Math.floor(1000 + Math.random() * 9000);
    const confirmation = prompt(`Para confirmar, digite o código: ${code}`);

    if (confirmation !== String(code)) {
      alert("Código incorreto. Ação cancelada.");
      return;
    }

    setLoadingAction(true);
    setLoadingMsg("Resetando estoque físico...");

    try {
      let hasMore = true;
      let deletedCount = 0;

      while (hasMore) {
        const batch = await pb.collection("inventory_items").getList(1, 50, {
          filter: 'status = "in_stock" && isPE = false',
        });

        if (batch.items.length === 0) {
          hasMore = false;
          break;
        }

        const updatePromises = batch.items.map((item) =>
          pb.collection("inventory_items").update(item.id, {
            status: "adjusted_out",
            dateOut: new Date().toISOString(),
            removedBy: "Sistema (Reset Geral)",
            reason: "mass_reset",
          })
        );
        await Promise.all(updatePromises);

        deletedCount += batch.items.length;
        setLoadingMsg(`Excluindo... (${deletedCount} itens)`);
      }

      alert(`Sucesso! ${deletedCount} itens físicos foram removidos do estoque.`);
    } catch (error) {
      console.error(error);
      alert("Erro ao resetar estoque: " + error.message);
    } finally {
      setLoadingAction(false);
    }
  };

  // --- CRUD USUÁRIOS ----
  const handleEdit = (user) => {
    const userAccess = user.acess || user.access || [];

    setFormData({
      id: user.id,
      name: user.name || "",
      username: user.username || "",
      email: user.email || "",
      phone: user.phone || "",
      password: "",
      oldPassword: "",
      role: user.role || "user",
      acess: Array.isArray(userAccess) ? userAccess : [],
      avatar: null,
      avatarUrl: user.avatar ? pb.files.getURL(user, user.avatar) : "",
      taxa_motoboy: user.taxa_motoboy || 0,
    });
    setIsEditing(true);
  };

  const handleNew = () => {
    setFormData({
      id: null,
      name: "",
      username: "",
      email: "",
      phone: "",
      password: "",
      oldPassword: "",
      role: "user",
      acess: ["stock"],
      avatar: null,
      avatarUrl: "",
      taxa_motoboy: 0,
    });
    setIsEditing(true);
  };

  const toggleAccess = (moduleId) => {
    setFormData((prev) => {
      const current = new Set(prev.acess);
      if (current.has(moduleId)) current.delete(moduleId);
      else current.add(moduleId);
      return { ...prev, acess: Array.from(current) };
    });
  };

  const applyPreset = (presetType) => {
    let moduleIds = [];
    if (presetType === "all") {
      moduleIds = AVAILABLE_MODULES.map((m) => m.id);
    } else if (presetType === "none") {
      moduleIds = [];
    } else if (presetType === "operacao") {
      moduleIds = ["production", "orders", "postagem", "reservations", "transit"];
    } else if (presetType === "estoque") {
      moduleIds = ["stock", "stock_production", "supplies", "planejamento", "conference", "sales"];
    } else if (presetType === "criacao") {
      moduleIds = ["oficina", "novos_produtos"];
    } else if (presetType === "gestao") {
      moduleIds = ["dashboard", "financeiro", "fiscal", "reports", "colaboradores", "pontuacao"];
    }
    setFormData((prev) => ({
      ...prev,
      acess: moduleIds,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoadingAction(true);
    setLoadingMsg("Salvando usuário...");

    if (!formData.username || (!formData.id && !formData.email)) {
      alert("Username e Email são obrigatórios.");
      setLoadingAction(false);
      return;
    }

    if (formData.password && formData.password.length < 8) {
      alert("A senha deve ter no mínimo 8 caracteres.");
      setLoadingAction(false);
      return;
    }

    if (!formData.id && !formData.password) {
      alert("Para novos usuários, a senha é obrigatória (mínimo 8 caracteres).");
      setLoadingAction(false);
      return;
    }

    if (requiresOldPassword && !formData.oldPassword) {
      alert(
        "Para alterar seu username ou senha, você deve preencher a 'Senha Atual' para confirmação de segurança."
      );
      setLoadingAction(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        role: formData.role,
        acess: formData.acess,
        access: formData.acess,
        taxa_motoboy: Number(formData.taxa_motoboy) || 0,
      };

      if (!formData.id || hasUsernameChanged) {
        payload.username = formData.username.trim();
      }

      if (!formData.id) {
        payload.email = formData.email;
      }

      if (hasPasswordChanged) {
        payload.password = formData.password;
        payload.passwordConfirm = formData.password;
      }

      if (requiresOldPassword && formData.oldPassword) {
        payload.oldPassword = formData.oldPassword;
      }

      if (formData.avatar) {
        payload.avatar = formData.avatar;
      }

      if (formData.id) {
        await pb.collection("users").update(formData.id, payload);
      } else {
        await pb.collection("users").create(payload);
      }

      alert(formData.id ? "Usuário atualizado com sucesso!" : "Usuário criado com sucesso!");
      setIsEditing(false);
    } catch (error) {
      console.error("Erro completo ao salvar usuário:", error);
      let detail = error.message;
      if (error.data && error.data.data) {
        detail +=
          "\n\nDetalhes de Validação:\n" +
          Object.entries(error.data.data)
            .map(([field, err]) => `- ${field}: ${err.message || JSON.stringify(err)}`)
            .join("\n");
      }
      alert("Erro ao salvar: " + detail);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente apagar este usuário?")) return;
    try {
      await pb.collection("users").delete(id);
    } catch (e) {
      alert("Erro: " + e.message);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        avatar: file,
        avatarUrl: URL.createObjectURL(file),
      }));
    }
  };

  // --- ESTRUTURA CATEGORIZADA DA SIDEBAR ---
  const CONFIG_SECTIONS = useMemo(
    () => [
      {
        category: "Geral & Acesso",
        items: [
          {
            id: "usuarios",
            label: "Usuários & Acessos",
            desc: "Permissões e controle de equipe",
            icon: Users,
            color: "text-blue-600 bg-blue-50",
            badge: users.length > 0 ? `${users.length}` : null,
          },
          {
            id: "geral",
            label: "Sistema & Catálogo",
            desc: "Manutenção, planejamento e carga",
            icon: Settings,
            color: "text-slate-600 bg-slate-100",
          },
          {
            id: "ia",
            label: "Inteligência Artificial",
            desc: "Provedores Claude, OpenAI e Gemini",
            icon: Shield,
            color: "text-indigo-600 bg-indigo-50",
          },
        ],
      },
      {
        category: "Oficina & Produção",
        items: [
          {
            id: "prazos",
            label: "Prazos & Calendário",
            desc: "Modelos, matriz e feriados SP",
            icon: Clock,
            color: "text-amber-600 bg-amber-50",
          },
          {
            id: "operadores",
            label: "Operadores (Oficina)",
            desc: "Operadores de bancada e PINs",
            icon: UserCheck,
            color: "text-emerald-600 bg-emerald-50",
          },
        ],
      },
      {
        category: "Comunicação Interna",
        items: [
          {
            id: "equipes",
            label: "Equipes & Times",
            desc: "Grupos e atribuições",
            icon: Users,
            color: "text-purple-600 bg-purple-50",
          },
          {
            id: "canais",
            label: "Canais de Chat",
            desc: "Salas e mensagens internas",
            icon: MessageSquare,
            color: "text-cyan-600 bg-cyan-50",
          },
        ],
      },
      {
        category: "Logística & Fiscal",
        items: [
          {
            id: "correios",
            label: "Correios & Fretes",
            desc: "Contrato e chaves CWS",
            icon: Truck,
            color: "text-orange-600 bg-orange-50",
          },
          {
            id: "fiscal",
            label: "Fiscal & NF-e",
            desc: "Emissor e certificados A1",
            icon: FileText,
            color: "text-emerald-600 bg-emerald-50",
          },
        ],
      },
    ],
    [users.length]
  );

  // Filtro de busca na sidebar
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return CONFIG_SECTIONS;
    const query = searchQuery.toLowerCase().trim();

    return CONFIG_SECTIONS.map((sec) => ({
      ...sec,
      items: sec.items.filter(
        (it) =>
          it.label.toLowerCase().includes(query) ||
          it.desc.toLowerCase().includes(query) ||
          sec.category.toLowerCase().includes(query)
      ),
    })).filter((sec) => sec.items.length > 0);
  }, [CONFIG_SECTIONS, searchQuery]);

  // Informações do item ativo para o cabeçalho contextual
  const activeItemInfo = useMemo(() => {
    for (const sec of CONFIG_SECTIONS) {
      const found = sec.items.find((i) => i.id === configActiveSubTab);
      if (found) return { ...found, category: sec.category };
    }
    return {
      id: "usuarios",
      label: "Usuários & Acessos",
      desc: "Permissões e controle de equipe",
      icon: Users,
      category: "Geral & Acesso",
    };
  }, [CONFIG_SECTIONS, configActiveSubTab]);

  const ActiveIcon = activeItemInfo.icon;

  return (
    <div className="flex h-full w-full overflow-hidden bg-slate-100/70">
      {loadingAction && <LoadingOverlay message={loadingMsg} />}

      {/* OVERLAY PARA MOBILE QUANDO A SIDEBAR ESTIVER ABERTA */}
      {sidebarOpenMobile && (
        <div
          onClick={() => setSidebarOpenMobile(false)}
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-xs md:hidden"
        />
      )}

      {/* ======================================================== */}
      {/* SIDEBAR DE CONFIGURAÇÕES                                 */}
      {/* ======================================================== */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200/90 flex flex-col shrink-0 h-full shadow-lg md:shadow-none transition-transform duration-200 ease-in-out ${
          sidebarOpenMobile ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Topo da Sidebar */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/20">
              <Settings size={18} />
            </div>
            <div>
              <h2 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                Configurações
              </h2>
              <p className="text-[11px] text-slate-500 font-medium">Painel Administrativo</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
              Admin
            </span>
            <button
              onClick={() => setSidebarOpenMobile(false)}
              className="p-1 text-slate-400 hover:text-slate-600 md:hidden cursor-pointer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Campo de Busca Rápida na Sidebar */}
        <div className="p-3 border-b border-slate-100 bg-white">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar configuração..."
              className="w-full pl-8 pr-7 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-slate-700 placeholder:text-slate-400 font-medium"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Itens de Navegação por Categoria */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4 custom-scrollbar">
          {filteredSections.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">
              Nenhuma configuração encontrada para "{searchQuery}".
            </div>
          ) : (
            filteredSections.map((section, sIdx) => (
              <div key={sIdx} className="space-y-1">
                <div className="px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {section.category}
                </div>
                {section.items.map((item) => {
                  const isActive = configActiveSubTab === item.id;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setConfigActiveSubTab(item.id);
                        setSidebarOpenMobile(false);
                      }}
                      className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left transition-all cursor-pointer group ${
                        isActive
                          ? "bg-blue-600 text-white shadow-sm shadow-blue-500/25 font-bold"
                          : "text-slate-600 hover:bg-slate-100/90 hover:text-slate-900 font-medium"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={`p-1.5 rounded-lg transition-colors shrink-0 ${
                            isActive ? "bg-white/20 text-white" : item.color
                          }`}
                        >
                          <Icon size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs truncate font-bold leading-tight">
                            {item.label}
                          </div>
                          <div
                            className={`text-[10px] truncate leading-tight mt-0.5 ${
                              isActive ? "text-blue-100" : "text-slate-500"
                            }`}
                          >
                            {item.desc}
                          </div>
                        </div>
                      </div>
                      {item.badge && (
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full font-bold ml-1.5 shrink-0 ${
                            isActive
                              ? "bg-white/20 text-white"
                              : "bg-slate-100 text-slate-600 group-hover:bg-slate-200"
                          }`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Rodapé da Sidebar */}
        <div className="p-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between text-[11px] text-slate-500">
          <span className="font-mono text-[10px]">Sempre Joias v1.1</span>
          <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Online
          </span>
        </div>
      </aside>

      {/* ======================================================== */}
      {/* ÁREA PRINCIPAL DE CONTEÚDO                               */}
      {/* ======================================================== */}
      <main className="flex-1 flex flex-col h-full overflow-hidden min-w-0">
        {/* Cabeçalho Contextual da Área de Conteúdo */}
        <header className="px-6 py-4 bg-white border-b border-slate-200/80 shrink-0 flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-3 min-w-0">
            {/* Botão Hambúrguer para Telas Menores */}
            <button
              onClick={() => setSidebarOpenMobile(true)}
              className="p-2 -ml-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg md:hidden cursor-pointer"
              title="Abrir menu lateral"
            >
              <Menu size={18} />
            </button>

            <div className="p-2 rounded-xl bg-blue-50 text-blue-600 shrink-0">
              <ActiveIcon size={20} />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium truncate">
                <span>Configurações</span>
                <ChevronRight size={12} className="text-slate-400" />
                <span className="text-slate-600 font-semibold">{activeItemInfo.category}</span>
                <ChevronRight size={12} className="text-slate-400" />
                <span className="text-blue-600 font-bold">{activeItemInfo.label}</span>
              </div>
              <h1 className="text-base md:text-lg font-black text-slate-800 tracking-tight leading-tight truncate">
                {activeItemInfo.label}
              </h1>
            </div>
          </div>

          {/* Ação de Novo Usuário quando estiver na aba de Usuários */}
          {configActiveSubTab === "usuarios" && !isEditing && (
            <button
              onClick={handleNew}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition-all cursor-pointer hover:scale-[1.02]"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Novo Usuário</span>
            </button>
          )}
        </header>

        {/* Corpo com Scroll Suave */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
          <div className="max-w-6xl mx-auto space-y-8 animate-fade-in pb-20">
            {/* ======================================================== */}
            {/* 1. GESTÃO DE USUÁRIOS & ACESSOS                         */}
            {/* ======================================================== */}
            {configActiveSubTab === "usuarios" && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-slate-100 bg-slate-50/60 flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                      <Users size={18} className="text-blue-600" />
                      {isEditing
                        ? formData.id
                          ? "Editar Usuário"
                          : "Novo Usuário"
                        : "Usuários Cadastrados"}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {isEditing
                        ? "Preencha os dados e permissões do colaborador."
                        : "Gerencie logins, senhas, níveis de acesso e permissões das 21 abas."}
                    </p>
                  </div>
                  {isEditing && (
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-3 py-1.5 border border-slate-300 rounded-lg text-slate-600 font-bold hover:bg-slate-100 text-xs transition-colors cursor-pointer flex items-center gap-1.5"
                    >
                      <ArrowLeft size={14} />
                      <span>Voltar para Lista</span>
                    </button>
                  )}
                </div>

                <div className="p-6">
                  {isEditing ? (
                    <form onSubmit={handleSave} className="space-y-8">
                      {/* Avatar e Campos de Texto */}
                      <div className="flex flex-col md:flex-row gap-8">
                        {/* Avatar */}
                        <div className="flex flex-col items-center gap-3 shrink-0">
                          <div className="relative w-32 h-32 rounded-full border-4 border-slate-100 shadow-inner overflow-hidden bg-slate-50 group">
                            {formData.avatarUrl ? (
                              <img
                                src={formData.avatarUrl}
                                alt="Avatar Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                <Users size={40} />
                              </div>
                            )}
                            <label className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                              <Camera size={24} />
                              <span className="text-[10px] font-bold mt-1">ALTERAR</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                              />
                            </label>
                          </div>
                          <span className="text-xs text-slate-400 font-medium">Foto de Perfil</span>
                        </div>

                        {/* Campos de Texto */}
                        <div className="flex-1 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                Nome Completo
                              </label>
                              <input
                                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.name}
                                onChange={(e) =>
                                  setFormData({ ...formData, name: e.target.value })
                                }
                                required
                                placeholder="Ex: João Silva"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                Username (Login)
                              </label>
                              <input
                                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                                value={formData.username}
                                onChange={(e) =>
                                  setFormData({ ...formData, username: e.target.value })
                                }
                                required
                                placeholder="Ex: joao.silva"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                Email
                              </label>
                              <div className="relative">
                                <Mail
                                  size={16}
                                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                  className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                  type="email"
                                  value={formData.email}
                                  onChange={(e) =>
                                    setFormData({ ...formData, email: e.target.value })
                                  }
                                  placeholder="email@exemplo.com"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                Telefone / Whatsapp
                              </label>
                              <div className="relative">
                                <Phone
                                  size={16}
                                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                  className="w-full pl-9 p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                  value={formData.phone}
                                  onChange={(e) =>
                                    setFormData({ ...formData, phone: e.target.value })
                                  }
                                  placeholder="(11) 99999-9999"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                Senha{" "}
                                {formData.id && (
                                  <span className="text-orange-500 normal-case font-normal">
                                    (Deixe em branco para manter)
                                  </span>
                                )}
                              </label>
                              <input
                                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                type="password"
                                value={formData.password}
                                onChange={(e) =>
                                  setFormData({ ...formData, password: e.target.value })
                                }
                                placeholder={formData.id ? "••••••••" : "Defina uma senha"}
                                autoComplete="new-password"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                Nível de Acesso (Role)
                              </label>
                              <select
                                className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.role}
                                onChange={(e) =>
                                  setFormData({ ...formData, role: e.target.value })
                                }
                              >
                                <option value="user">USER (Padrão)</option>
                                <option value="master">MASTER (Admin Total)</option>
                                <option value="gestor_estoque">GESTOR DE ESTOQUE</option>
                                <option value="motoboy">MOTOBOY (Entregador)</option>
                              </select>
                            </div>
                            {formData.role === "motoboy" && (
                              <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                  Taxa por KM (R$)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="w-full p-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                                  value={
                                    formData.taxa_motoboy === 0
                                      ? ""
                                      : formData.taxa_motoboy
                                  }
                                  onChange={(e) =>
                                    setFormData({ ...formData, taxa_motoboy: e.target.value })
                                  }
                                  placeholder="Ex: 2.50"
                                />
                              </div>
                            )}
                          </div>

                          {requiresOldPassword && (
                            <div className="bg-red-50/50 p-4 rounded-xl border border-red-200 animate-in fade-in slide-in-from-top duration-200">
                              <label className="block text-xs font-bold text-red-700 uppercase mb-1 flex items-center gap-1.5">
                                <Lock size={14} /> Confirmação de Segurança: Digite a Senha Atual
                              </label>
                              <input
                                className="w-full md:max-w-md p-2.5 border border-red-300 focus:border-red-500 rounded-lg text-sm outline-none focus:ring-2 focus:ring-red-100 bg-white text-slate-800 font-mono"
                                type="password"
                                value={formData.oldPassword}
                                onChange={(e) =>
                                  setFormData({ ...formData, oldPassword: e.target.value })
                                }
                                placeholder="Senha atual para autorizar alteração"
                                required
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Permissões */}
                      <div className="bg-slate-50/70 p-5 rounded-2xl border border-slate-200 space-y-5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200">
                          <div>
                            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                              <Shield size={16} className="text-blue-600" /> Permissões de Módulo ({formData.acess.length} selecionados)
                            </h4>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Selecione as áreas que este usuário terá permissão para visualizar e operar.
                            </p>
                          </div>

                          {/* Botões de atalho rápido */}
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">Atalhos:</span>
                            <button
                              type="button"
                              onClick={() => applyPreset("all")}
                              className="px-2.5 py-1 text-[11px] font-bold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-100 cursor-pointer shadow-2xs"
                            >
                              Marcar Todos
                            </button>
                            <button
                              type="button"
                              onClick={() => applyPreset("none")}
                              className="px-2.5 py-1 text-[11px] font-bold bg-white border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-100 cursor-pointer shadow-2xs"
                            >
                              Limpar
                            </button>
                            <button
                              type="button"
                              onClick={() => applyPreset("operacao")}
                              className="px-2.5 py-1 text-[11px] font-bold bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 cursor-pointer shadow-2xs"
                            >
                              Operação
                            </button>
                            <button
                              type="button"
                              onClick={() => applyPreset("estoque")}
                              className="px-2.5 py-1 text-[11px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100 cursor-pointer shadow-2xs"
                            >
                              Estoque
                            </button>
                            <button
                              type="button"
                              onClick={() => applyPreset("gestao")}
                              className="px-2.5 py-1 text-[11px] font-bold bg-amber-50 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-100 cursor-pointer shadow-2xs"
                            >
                              Gestão
                            </button>
                          </div>
                        </div>

                        {/* Categorias de Módulos */}
                        <div className="space-y-4">
                          {MODULE_CATEGORIES.map((cat) => (
                            <div key={cat.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs">
                              <div className="flex items-center justify-between mb-3">
                                <span className={`text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-md border ${cat.badgeColor}`}>
                                  {cat.title}
                                </span>
                                <span className="text-[11px] text-slate-400">
                                  {cat.modules.filter((m) => formData.acess.includes(m.id)).length} de {cat.modules.length} liberados
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                                {cat.modules.map((mod) => {
                                  const hasAccess = formData.acess.includes(mod.id);
                                  return (
                                    <div
                                      key={mod.id}
                                      onClick={() => toggleAccess(mod.id)}
                                      className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all ${
                                        hasAccess
                                          ? "bg-blue-50/80 border-blue-300 shadow-xs"
                                          : "bg-slate-50/50 border-slate-200 hover:border-slate-300 hover:bg-white"
                                      }`}
                                    >
                                      <div
                                        className={`flex items-center justify-center w-5 h-5 rounded mt-0.5 shrink-0 transition-colors ${
                                          hasAccess ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-400"
                                        }`}
                                      >
                                        {hasAccess ? <CheckSquare size={13} /> : <Square size={13} />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className={`text-xs font-bold truncate ${hasAccess ? "text-blue-900" : "text-slate-700"}`}>
                                            {mod.label}
                                          </span>
                                          {mod.isSubFeature && (
                                            <span className="text-[9px] font-bold text-amber-700 bg-amber-50 px-1 py-0.2 rounded border border-amber-200 shrink-0">
                                              Sub-recurso
                                            </span>
                                          )}
                                        </div>
                                        {mod.desc && (
                                          <p className="text-[10px] text-slate-400 leading-tight mt-0.5 line-clamp-1">
                                            {mod.desc}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Botões Salvar */}
                      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => setIsEditing(false)}
                          className="px-5 py-2.5 border border-slate-300 rounded-xl text-slate-600 font-bold hover:bg-slate-50 transition-colors uppercase text-xs tracking-wide cursor-pointer"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="px-7 py-2.5 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 flex items-center gap-2 shadow-md shadow-green-500/20 transition-all transform hover:scale-[1.02] uppercase text-xs tracking-wide cursor-pointer"
                        >
                          <Save size={16} /> Salvar Usuário
                        </button>
                      </div>
                    </form>
                  ) : (
                    /* Tabela de Usuários */
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm border-separate border-spacing-y-2">
                        <thead className="text-slate-400 font-bold uppercase text-[10px] tracking-wider">
                          <tr>
                            <th className="px-4 py-2">Usuário</th>
                            <th className="px-4 py-2">Contato</th>
                            <th className="px-4 py-2">Nível</th>
                            <th className="px-4 py-2 text-center">Permissões</th>
                            <th className="px-4 py-2 text-right">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="space-y-2">
                          {users.map((u) => {
                            const uAccess = u.acess || u.access || [];
                            return (
                              <tr
                                key={u.id}
                                className="bg-white hover:bg-slate-50/80 shadow-xs rounded-xl transition-colors group"
                              >
                                <td className="px-4 py-3 rounded-l-xl border-y border-l border-slate-200">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 shrink-0">
                                      {u.avatar ? (
                                        <img
                                          src={pb.files.getURL(u, u.avatar)}
                                          className="w-full h-full object-cover"
                                          alt="Avatar"
                                        />
                                      ) : (
                                        <Users size={20} className="text-slate-400" />
                                      )}
                                    </div>
                                    <div>
                                      <div className="font-bold text-slate-800 text-xs md:text-sm">
                                        {u.name}
                                      </div>
                                      <div className="text-[11px] text-slate-500 font-mono">
                                        @{u.username}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 border-y border-slate-200">
                                  <div className="text-xs text-slate-600">
                                    {u.email && (
                                      <div className="flex items-center gap-1.5 mb-0.5">
                                        <Mail size={12} className="text-slate-400" /> {u.email}
                                      </div>
                                    )}
                                    {u.phone && (
                                      <div className="flex items-center gap-1.5">
                                        <Phone size={12} className="text-slate-400" /> {u.phone}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 border-y border-slate-200">
                                  {u.role === "master" ? (
                                    <span className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border border-amber-200">
                                      MASTER
                                    </span>
                                  ) : u.role === "motoboy" ? (
                                    <span className="bg-purple-100 text-purple-800 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border border-purple-200">
                                      MOTOBOY
                                    </span>
                                  ) : u.role === "gestor_estoque" ? (
                                    <span className="bg-blue-100 text-blue-800 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border border-blue-200">
                                      GESTOR ESTOQUE
                                    </span>
                                  ) : (
                                    <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border border-slate-200">
                                      USER
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 border-y border-slate-200 text-center">
                                  {u.role === "master" ? (
                                    <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                                      Acesso Total (Master)
                                    </span>
                                  ) : (
                                    <span className="text-[11px] font-bold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">
                                      {uAccess.length} Módulos
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 rounded-r-xl border-y border-r border-slate-200 text-right">
                                  <div className="flex justify-end gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => handleEdit(u)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                                      title="Editar Usuário"
                                    >
                                      <RefreshCw size={15} />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(u.id)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                      title="Apagar Usuário"
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ======================================================== */}
            {/* 2. SISTEMA, MANUTENÇÃO & CATÁLOGO (GERAL)                */}
            {/* ======================================================== */}
            {configActiveSubTab === "geral" && (
              <div className="space-y-6">
                {/* ZONA DE SISTEMA & MANUTENÇÃO */}
                <div className="bg-white p-6 rounded-2xl border border-red-100 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm md:text-base font-bold text-red-700 flex items-center gap-2">
                      <AlertTriangle size={20} /> Sistema & Manutenção do Banco
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Ferramentas avançadas de gestão e saneamento do estoque físico da Sempre Joias.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={handleResetStock}
                      className="flex items-center gap-2 bg-white text-red-700 px-4 py-2.5 rounded-xl text-xs font-bold border border-red-300 hover:bg-red-600 hover:text-white transition-all shadow-xs cursor-pointer"
                    >
                      <RefreshCw size={15} /> RESETAR ESTOQUE FÍSICO
                    </button>
                  </div>
                </div>

                {/* CONFIGURAÇÃO DO PLANEJAMENTO */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm md:text-base font-bold text-slate-800 flex items-center gap-2">
                      <Settings size={20} className="text-blue-600" /> Configurações do Planejamento
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Defina o prazo padrão de fabricação para o módulo de Planejamento de Estoque.
                    </p>
                  </div>

                  <div className="flex items-center gap-3 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                      Prazo de Fabricação (Dias):
                    </span>
                    <input
                      type="number"
                      min="1"
                      className="w-20 p-2 bg-white border border-slate-300 rounded-lg text-sm font-mono text-center outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                      value={prazoDias}
                      onChange={(e) => handleSavePrazo(e.target.value)}
                    />
                  </div>
                </div>

                {/* IMPORTADOR DE CATÁLOGO */}
                <CatalogImporter />
              </div>
            )}

            {/* ======================================================== */}
            {/* 3. INTELIGÊNCIA ARTIFICIAL                              */}
            {/* ======================================================== */}
            {configActiveSubTab === "ia" && (
              <div className="space-y-6">
                <IaConfigSubTab />
              </div>
            )}

            {/* ======================================================== */}
            {/* 4. FISCAL & NF-E                                         */}
            {/* ======================================================== */}
            {configActiveSubTab === "fiscal" && (
              <div className="space-y-6">
                <FiscalConfigPanel />
              </div>
            )}

            {/* ======================================================== */}
            {/* 5. CORREIOS & FRETES                                     */}
            {/* ======================================================== */}
            {configActiveSubTab === "correios" && (
              <div className="space-y-6">
                <CorreiosConfigPanel />
              </div>
            )}

            {/* ======================================================== */}
            {/* 6. OPERADORES (OFICINA)                                  */}
            {/* ======================================================== */}
            {configActiveSubTab === "operadores" && (
              <div className="space-y-6">
                <OperatorsConfigPanel />
              </div>
            )}

            {/* ======================================================== */}
            {/* 7. EQUIPES & TIMES                                       */}
            {/* ======================================================== */}
            {configActiveSubTab === "equipes" && (
              <div className="space-y-6">
                <TeamsConfigPanel />
              </div>
            )}

            {/* ======================================================== */}
            {/* 8. CANAIS DE CHAT                                        */}
            {/* ======================================================== */}
            {configActiveSubTab === "canais" && (
              <div className="space-y-6">
                <ChannelsConfigPanel />
              </div>
            )}

            {/* ======================================================== */}
            {/* 9. PRAZOS & CALENDÁRIO (OFICINA)                         */}
            {/* ======================================================== */}
            {configActiveSubTab === "prazos" && (
              <div className="space-y-6">
                <PrazosConfigTab />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}